import React, { useEffect } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';

import { createCampaign, updateCampaign } from 'services/api/admin-campaigns';
import { deleteImagesFromCdn } from 'services/api/admin-images';
import { requestCampaigns } from 'services/actions/adminActions';
import {
  AdminSelect,
  AdminInput,
  AdminFormRow,
  AdminCheckbox,
} from 'shared-components';
import {
  showSuccessToast,
  showErrorToast,
} from 'shared-components/BaseToast/Toast';
import Button, { BUTTON_TYPES } from 'shared-components/Button/Button';
import {
  CAMPAIGN_TYPE,
  CAMPAIGN_WINDOWS,
  CAMPAIGN_TYPES,
  CAMPAIGN_WINDOWS_LIST,
} from 'utils/campaign';
import { formatDate, checkDateIsAfter } from 'utils/date';
import { handleValidation, validateDate } from 'utils/admin-forms';
import { useForm } from 'react-hook-form';
import CampaignBannerForm from '../CampaignBannerForm/CampaignBannerForm';
import styles from './CampaignForm.module.css';

const CampaignForm = ({
  getCampaigns,
  formType,
  formDisabled,
  campaignData,
  disableForm,
  setEditingForm,
  onClose,
  setSelectedCampaignData,
  selectedCampaign,
}) => {
  const {
    register,
    handleSubmit,
    setError,
    errors,
    watch,
    reset,
    setValue,
    formState,
    clearErrors,
  } = useForm({
    defaultValues: {
      isSocial: false,
      isPremiumSocial: false,
      isFree: false,
      isPremiumFree: false,
    },
  });
  const { isDirty, dirtyFields } = formState;

  const watchAllFields = watch();
  const isCreateForm = formType === 'create';
  const isEditForm = formType === 'edit';
  const {
    type,
    name,
    sponsoredThreshold,
    campaignWindows,
    endDate,
    premiumEndDate,
    premiumStartDate,
    startDate,
    bannerDesktopImageUrl,
    isSocial,
    isPremiumSocial,
    isFree,
    isPremiumFree,
  } = watchAllFields;
  const isEditCampaign = type === CAMPAIGN_TYPE.edit;

  useEffect(() => {
    reset(campaignData);
  }, [reset, campaignData]);

  useEffect(() => {
    register({ name: 'bannerDesktopImageUrl' });
    register({ name: 'type' });
    // these fields need to be controlled because they sometimes turn into arrays
    register({ name: 'isPremiumSocial' });
    register({ name: 'isSocial' });
    register({ name: 'isFree' });
    register({ name: 'isPremiumFree' });
  }, [register]);

  useEffect(() => {
    if (isEditForm) {
      if (isDirty) {
        setEditingForm(true);
      } else {
        setEditingForm(false);
      }
    }
  }, [isDirty]);

  const validateDates = () => {
    let isValid = true;
    if (campaignWindows === CAMPAIGN_WINDOWS.one) {
      //check if premium start date is after end date
      if (checkDateIsAfter(premiumStartDate, endDate)) {
        setError('premiumStartDate', {
          type: 'manual',
          message: 'Start date must be before end date.',
        });
        isValid = false;
      }
      //check if premium start date is after premium end date
    } else if (checkDateIsAfter(premiumStartDate, premiumEndDate)) {
      setError('premiumStartDate', {
        type: 'manual',
        message: 'Start date must be before end date.',
      });
      isValid = false;
    }
    //check if start date is after end date
    if (checkDateIsAfter(startDate, endDate)) {
      setError('startDate', {
        type: 'manual',
        message: 'Start date must be before end date.',
      });
      isValid = false;
      //check if premium end date is after start date if there are two windows
    } else if (campaignWindows === CAMPAIGN_WINDOWS.two) {
      if (checkDateIsAfter(premiumEndDate, startDate)) {
        setError('startDate', {
          type: 'manual',
          message: 'Window 2 start date cannot be before Window 1 end date.',
        });
        isValid = false;
      }
      if (checkDateIsAfter(premiumStartDate, startDate)) {
        setError('premiumStartDate', {
          type: 'manual',
          message: 'Select start date must be after start date.',
        });
        isValid = false;
      }
    }
    return isValid;
  };

  const formatData = (formData) => {
    const submitData = { ...formData };
    submitData.sponsoredThreshold = Math.round(
      formData.sponsoredThreshold * 100
    );
    if (type !== CAMPAIGN_TYPE.shop) {
      submitData.startDate = formatDate(formData.startDate);
      submitData.endDate = formatDate(formData.endDate);
      submitData.premiumStartDate = formatDate(formData.premiumStartDate);
      submitData.premiumEndDate =
        formatDate(formData.premiumEndDate) || formatDate(formData.endDate);
    }
    if (type === CAMPAIGN_TYPE.edit) {
      if (formData.campaignWindows === CAMPAIGN_WINDOWS.one) {
        submitData.isSocial = formData.isPremiumSocial;
        submitData.isFree = formData.isPremiumFree;
      }
    }
    submitData.id = selectedCampaign;
    delete submitData.campaignWindows;
    return submitData;
  };

  const handleChangeType = (e) => {
    const {
      target: { value },
    } = e;

    const isChangingFromShop =
      type === CAMPAIGN_TYPE.shop &&
      (value === CAMPAIGN_TYPE.edit || value === CAMPAIGN_TYPE.season);

    const isChangingFromFlash =
      (type === CAMPAIGN_TYPE.edit || type === CAMPAIGN_TYPE.season) &&
      value === CAMPAIGN_TYPE.shop;

    if (isChangingFromShop || isChangingFromFlash) {
      setValue('bannerParagraph', '');
    }
    setValue('type', value);
  };

  const submitForm = async (formData) => {
    const submitData = formatData(formData);
    if (isCreateForm) {
      try {
        await createCampaign(submitData);
        getCampaigns();
        onClose();
        showSuccessToast(
          `${name} Has Been Successfully Created`,
          'Your changes have been successfully saved.'
        );
      } catch {
        showErrorToast(
          `Error Creating Campaign`,
          'Unable to create campaign. Please try again.'
        );
      }
    }
    if (isEditForm) {
      try {
        await updateCampaign(submitData);
        if (campaignData.bannerDesktopImageUrl !== bannerDesktopImageUrl)
          deleteImagesFromCdn([campaignData.bannerDesktopImageUrl]);

        showSuccessToast(
          `${name} Has Been Successfully Updated`,
          'Your changes have been successfully saved.'
        );
        getCampaigns();
        setEditingForm(false);
        setSelectedCampaignData(formData);
        disableForm();
      } catch {
        showErrorToast(
          `Error Updating Campaign`,
          'Unable to update campaign. Please try again.'
        );
      }
    }
  };

  const onSubmit = (data) => {
    if (type === CAMPAIGN_TYPE.shop) {
      submitForm(data);
    } else if (validateDates()) {
      submitForm(data);
    }
  };

  const handleCancel = () => {
    if (bannerDesktopImageUrl && dirtyFields.bannerDesktopImageUrl) {
      deleteImagesFromCdn([bannerDesktopImageUrl]);
    }
    onClose();
  };

  const handleChangeCheckbox = (e) => {
    const { target } = e;
    setValue(target.name, target.checked);
  };

  const checkButtonDisabled = () => {
    let buttonDisabled = false;
    if (type === CAMPAIGN_TYPE.shop) {
      if (!name || !sponsoredThreshold) {
        buttonDisabled = true;
      }
    } else {
      _.forOwn(watchAllFields, (value, key) => {
        if (value === '') {
          if (
            campaignWindows === CAMPAIGN_WINDOWS.one &&
            key === 'premiumEndDate' &&
            endDate !== ''
          ) {
            buttonDisabled = false;
          } else if (key === 'cartText') {
            buttonDisabled = false;
          } else {
            buttonDisabled = true;
          }
        }
      });
    }
    return buttonDisabled;
  };

  const saveDisabled = isCreateForm
    ? checkButtonDisabled()
    : false || !_.isEmpty(errors);

  const saveButtonType = isCreateForm
    ? BUTTON_TYPES.TERTIARY
    : BUTTON_TYPES.PRIMARY;

  return (
    <div className={isCreateForm ? styles.createForm : ''}>
      {isCreateForm && (
        <AdminFormRow>
          <h1>Create a Campaign</h1>
        </AdminFormRow>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <AdminFormRow>
          <AdminSelect
            label="Campaign Type"
            selectLabel="Select a Type"
            list={CAMPAIGN_TYPES}
            name="type"
            disabled={formDisabled}
            handleChange={handleChangeType}
            propRef={register({
              required: 'This field is required.',
            })}
            selected={type}
            error={_.get(errors, ['type', 'message'])}
          />
          {selectedCampaign && (
            <AdminInput
              label="Campaign ID"
              name="id"
              value={selectedCampaign}
              disabled
              style={{ width: '180px' }}
              type="text"
            />
          )}
          <AdminInput
            name="name"
            placeholder="Name of Campaign"
            type="text"
            disabled={formDisabled}
            label="Name of Campaign"
            propRef={register({
              required: 'This field is required.',
            })}
            error={_.get(errors, ['name', 'message'])}
          />
          <AdminInput
            name="sponsoredThreshold"
            placeholder="Threshold Price"
            disabled={formDisabled}
            label="Sponsored Product Price Threshold"
            type="price"
            propRef={register({
              required: 'This field is required.',
            })}
            error={_.get(errors, ['sponsoredThreshold', 'message'])}
          />
        </AdminFormRow>
        <AdminFormRow>
          <AdminInput
            label="Text in the Cart (HTML Supported)"
            name="cartText"
            placeholder="Input text and/or HTML"
            disabled={formDisabled}
            style={{ width: '894px' }}
            type="textArea"
            propRef={register}
            error={_.get(errors, ['cartText', 'message'])}
          />
        </AdminFormRow>
        {type !== CAMPAIGN_TYPE.shop && (
          <AdminFormRow>
            <AdminSelect
              label="Windows in Campaign"
              name="campaignWindows"
              disabled={formDisabled}
              selectLabel="Select Number"
              list={CAMPAIGN_WINDOWS_LIST}
              type="price"
              propRef={register({
                required: 'This field is required.',
              })}
              selected={campaignWindows}
              error={_.get(errors, ['campaignWindows', 'message'])}
            />
          </AdminFormRow>
        )}
        {campaignWindows === CAMPAIGN_WINDOWS.one && (
          <AdminFormRow>
            <AdminInput
              label="Select Start Date"
              name="premiumStartDate"
              placeholder="MM/DD/YYYY HH:MM:SS"
              disabled={formDisabled}
              type="text"
              propRef={register({
                required: 'This field is required.',
              })}
              onChange={(e) => {
                handleValidation(e, setError, clearErrors, validateDate);
              }}
              error={_.get(errors, ['premiumStartDate', 'message'])}
              style={{ width: 200 }}
            />
            <span className={styles.rowText}>/</span>
            <AdminInput
              label="Start Date"
              name="startDate"
              placeholder="MM/DD/YYYY HH:MM:SS"
              disabled={formDisabled}
              type="text"
              propRef={register({
                required: 'This field is required.',
              })}
              onChange={(e) => {
                handleValidation(e, setError, clearErrors, validateDate);
              }}
              error={_.get(errors, ['startDate', 'message'])}
              style={{ width: 200 }}
            />
            <span className={styles.rowText}>to</span>
            <AdminInput
              label="End Date"
              type="text"
              placeholder="MM/DD/YYYY HH:MM:SS"
              disabled={formDisabled}
              name="endDate"
              propRef={register({
                required: true,
              })}
              onChange={(e) => {
                handleValidation(e, setError, clearErrors, validateDate);
              }}
              error={_.get(errors, ['endDate', 'message'])}
              style={{ width: 200 }}
            />
            {isEditCampaign && (
              <>
                <div className={styles.socialWindow}>
                  <AdminCheckbox
                    name="isPremiumSocial"
                    checked={isPremiumSocial}
                    label="Social Window"
                    disabled={formDisabled}
                    onChange={handleChangeCheckbox}
                  />
                </div>
                <div className={styles.socialWindow}>
                  <AdminCheckbox
                    name="isPremiumFree"
                    checked={isPremiumFree}
                    label="Club Access Window"
                    disabled={formDisabled}
                    onChange={handleChangeCheckbox}
                  />
                </div>
              </>
            )}
          </AdminFormRow>
        )}
        {campaignWindows === CAMPAIGN_WINDOWS.two && (
          <>
            <p className={styles.labelBold}>Window 1 Dates (Select)</p>
            <AdminFormRow>
              <AdminInput
                label="Start Date"
                placeholder="MM/DD/YYYY HH:MM:SS"
                disabled={formDisabled}
                type="text"
                propRef={register({
                  required: 'This field is required.',
                })}
                name="premiumStartDate"
                onChange={(e) => {
                  handleValidation(e, setError, clearErrors, validateDate);
                }}
                error={_.get(errors, ['premiumStartDate', 'message'])}
                style={{ width: 200 }}
              />
              <span className={styles.rowText}>to</span>
              <AdminInput
                label="End Date"
                placeholder="MM/DD/YYYY HH:MM:SS"
                disabled={formDisabled}
                type="text"
                name="premiumEndDate"
                propRef={register({
                  required: 'This field is required.',
                })}
                onChange={(e) => {
                  handleValidation(e, setError, clearErrors, validateDate);
                }}
                error={_.get(errors, ['premiumEndDate', 'message'])}
                style={{ width: 200 }}
              />
              {isEditCampaign && (
                <>
                  <div className={styles.socialWindow}>
                    <AdminCheckbox
                      name="isPremiumSocial"
                      checked={isPremiumSocial}
                      label="Social Window"
                      disabled={formDisabled}
                      onChange={handleChangeCheckbox}
                    />
                  </div>
                  <div className={styles.socialWindow}>
                    <AdminCheckbox
                      name="isPremiumFree"
                      checked={isPremiumFree}
                      label="Club Access Window"
                      disabled={formDisabled}
                      onChange={handleChangeCheckbox}
                    />
                  </div>
                </>
              )}
            </AdminFormRow>
            <p className={styles.labelBold}>Window 2 Dates (All Users)</p>
            <AdminFormRow>
              <AdminInput
                label="Start Date"
                type="text"
                name="startDate"
                placeholder="MM/DD/YYYY HH:MM:SS"
                disabled={formDisabled}
                propRef={register({
                  required: 'This field is required.',
                })}
                onChange={(e) => {
                  handleValidation(e, setError, clearErrors, validateDate);
                }}
                error={_.get(errors, ['startDate', 'message'])}
                style={{ width: 200 }}
              />
              <span className={styles.rowText}>to</span>
              <AdminInput
                label="End Date"
                name="endDate"
                placeholder="MM/DD/YYYY HH:MM:SS"
                disabled={formDisabled}
                type="text"
                propRef={register({
                  required: 'This field is required.',
                })}
                onChange={(e) => {
                  handleValidation(e, setError, clearErrors, validateDate);
                }}
                error={_.get(errors, ['endDate', 'message'])}
                style={{ width: 200 }}
              />
              {isEditCampaign && (
                <>
                  <div className={styles.socialWindow}>
                    <AdminCheckbox
                      name="isSocial"
                      label="Social Window"
                      disabled={formDisabled}
                      propRef={register}
                      checked={isSocial}
                      onChange={handleChangeCheckbox}
                    />
                  </div>
                  <div className={styles.socialWindow}>
                    <AdminCheckbox
                      name="isFree"
                      label="Club Access Window"
                      disabled={formDisabled}
                      propRef={register}
                      checked={isFree}
                      onChange={handleChangeCheckbox}
                    />
                  </div>
                </>
              )}
            </AdminFormRow>
          </>
        )}
        {isEditForm && (
          <CampaignBannerForm
            register={register}
            formDisabled={formDisabled}
            errors={errors}
            setError={setError}
            clearErrors={clearErrors}
            setBannerDesktopUrl={(url) => {
              setValue('bannerDesktopImageUrl', url, { shouldDirty: true });
            }}
            bannerDesktopImageUrl={bannerDesktopImageUrl}
            isShopCampaign={type === CAMPAIGN_TYPE.shop}
          />
        )}
        {!formDisabled && (
          <AdminFormRow>
            <Button
              type="submit"
              buttonType={saveButtonType}
              disabled={saveDisabled}
              style={{ width: '120px' }}
            >
              Save
            </Button>
            <Button
              type="button"
              buttonType={BUTTON_TYPES.CANCEL}
              onClick={handleCancel}
              style={{ width: '120px' }}
            >
              Cancel
            </Button>
          </AdminFormRow>
        )}
      </form>
    </div>
  );
};
const mapDispatchToProps = {
  getCampaigns: requestCampaigns,
};

export default connect(null, mapDispatchToProps)(CampaignForm);
